import {createSelector, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {getResponseBody} from '@utils/apiResponse';
import {userApi} from '@api/user/api';
import type {UserEmailAlias} from '@api/user/api.d';
import type {AppDispatch, RootState} from '@src/client/index';
import {getEmailAliasErrorType} from './getEmailAliasErrorType';
import type {EmailAliasErrorType} from './getEmailAliasErrorType';

export type EmailAliasConfirmationStatus =
    | 'idle'
    | 'requesting'
    | 'codeSent'
    | 'cooldown'
    | 'confirming'
    | 'confirmed'
    | 'alreadyConfirmed'
    | 'notFound'
    | 'forbidden'
    | 'error';

export interface EmailAliasConfirmation {
    status: EmailAliasConfirmationStatus;
    codeErrorType: EmailAliasErrorType | null;
    alias: UserEmailAlias | null;
    aliasId: string | null;
}

export interface EmailAliasesState {
    aliases: UserEmailAlias[];
    isLoading: boolean;
    aliasesUserId: string | null;
    isAdding: boolean;
    busyAliasIds: string[];
    confirmation: EmailAliasConfirmation;
}

const initialConfirmation: EmailAliasConfirmation = {
    status: 'idle', codeErrorType: null, alias: null, aliasId: null
};

const initialState: EmailAliasesState = {
    aliases: [],
    isLoading: false,
    aliasesUserId: null,
    isAdding: false,
    busyAliasIds: [],
    confirmation: initialConfirmation
};

type AliasResponse = UserEmailAlias & {confirmation_token?: unknown};

/** The confirmation token arrives in API responses and must never reach the store. */
const getAliasWithoutToken = (alias: AliasResponse): UserEmailAlias => {
    const aliasWithoutToken = {...alias};
    delete aliasWithoutToken.confirmation_token;
    return aliasWithoutToken;
};

export const emailAliasesSlice = createSlice({
    name: 'emailAliases',
    initialState,
    reducers: {
        setAliases: (state, action: PayloadAction<UserEmailAlias[]>) => {
            state.aliases = action.payload;
        },
        setIsLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setAliasesUserId: (state, action: PayloadAction<string | null>) => {
            state.aliasesUserId = action.payload;
        },
        setIsAdding: (state, action: PayloadAction<boolean>) => {
            state.isAdding = action.payload;
        },
        addBusyAliasId: (state, action: PayloadAction<string>) => {
            state.busyAliasIds.push(action.payload);
        },
        removeBusyAliasId: (state, action: PayloadAction<string>) => {
            state.busyAliasIds = state.busyAliasIds.filter((aliasId) => aliasId !== action.payload);
        },
        setConfirmation: (state, action: PayloadAction<Partial<EmailAliasConfirmation>>) => {
            state.confirmation = {...state.confirmation, ...action.payload};
        },
        resetEmailAliasConfirmation: (state) => {
            state.confirmation = initialConfirmation;
        },
        clearEmailAliasCodeError: (state) => {
            state.confirmation.codeErrorType = null;
        }
    }
});

export const {
    setAliases,
    setIsLoading,
    setAliasesUserId,
    setIsAdding,
    addBusyAliasId,
    removeBusyAliasId,
    setConfirmation,
    resetEmailAliasConfirmation,
    clearEmailAliasCodeError
} = emailAliasesSlice.actions;

export const EMAIL_ALIAS_CODE_COOLDOWN_MINUTES = 5;
export const EMAIL_ALIAS_POLL_INTERVAL_MS = 2000;

const getUserId = (getState: () => RootState): string => (getState().user.user as {id: string}).id;

const getCurrentUserId = (getState: () => RootState): string | null => (getState().user.user as {id?: string} | null)?.id ?? null;

const getIsCurrentConfirmation = (getState: () => RootState, aliasId: string): boolean => getState().emailAliases.confirmation.aliasId === aliasId;

const getAliasesFromApi = async (userId: string): Promise<UserEmailAlias[]> => {
    const response = await userApi.getEmailAliases({userId});
    return (getResponseBody<AliasResponse[]>(response) ?? []).map(getAliasWithoutToken);
};

/** A failed list refresh after a successful mutation must not be reported as a failed mutation. */
const setAliasesFromApi = async (dispatch: AppDispatch, userId: string): Promise<void> => {
    await getAliasesFromApi(userId).then((aliases) => dispatch(setAliases(aliases))).catch(() => undefined);
};

export const fetchEmailAliases = () => async (dispatch: AppDispatch, getState: () => RootState): Promise<EmailAliasErrorType | null> => {
    dispatch(setIsLoading(true));
    const userId = getCurrentUserId(getState);
    try {
        if (!userId) return null;
        const aliases = await getAliasesFromApi(userId);
        if (getCurrentUserId(getState) === userId) dispatch(setAliases(aliases));
        return null;
    } catch (error) {
        if (getCurrentUserId(getState) === userId && getState().emailAliases.aliasesUserId !== userId) dispatch(setAliases([]));
        return getEmailAliasErrorType(error);
    } finally {
        if (userId && getCurrentUserId(getState) === userId) dispatch(setAliasesUserId(userId));
        dispatch(setIsLoading(false));
    }
};

export const addEmailAlias = (email: string) => async (dispatch: AppDispatch, getState: () => RootState): Promise<EmailAliasErrorType | null> => {
    const userId = getUserId(getState);
    dispatch(setIsAdding(true));
    try {
        await userApi.createEmailAlias({userId, data: {email}});
        await setAliasesFromApi(dispatch, userId);
        return null;
    } catch (error) {
        return getEmailAliasErrorType(error);
    } finally {
        dispatch(setIsAdding(false));
    }
};

export const removeEmailAlias = (aliasId: string) => async (dispatch: AppDispatch, getState: () => RootState): Promise<EmailAliasErrorType | null> => {
    const userId = getUserId(getState);
    dispatch(addBusyAliasId(aliasId));
    try {
        await userApi.deleteEmailAlias({userId, aliasId});
        await setAliasesFromApi(dispatch, userId);
        return null;
    } catch (error) {
        const errorType = getEmailAliasErrorType(error);
        if (errorType === 'notFound') await setAliasesFromApi(dispatch, userId);
        return errorType;
    } finally {
        dispatch(removeBusyAliasId(aliasId));
    }
};

export const resendEmailAliasConfirmationCode = (aliasId: string) => async (dispatch: AppDispatch, getState: () => RootState): Promise<EmailAliasErrorType | null> => {
    const userId = getUserId(getState);
    dispatch(addBusyAliasId(aliasId));
    try {
        await userApi.requestEmailAliasConfirmationCode({userId, aliasId});
        await setAliasesFromApi(dispatch, userId);
        return null;
    } catch (error) {
        const errorType = getEmailAliasErrorType(error);
        if (errorType === 'notFound') await setAliasesFromApi(dispatch, userId);
        return errorType;
    } finally {
        dispatch(removeBusyAliasId(aliasId));
    }
};

/** Silent refresh for confirmation polling; skipped while a mutation is in flight so it cannot resurrect stale rows. */
export const pollEmailAliases = () => async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const {isAdding, busyAliasIds} = getState().emailAliases;
    if (isAdding || busyAliasIds.length > 0) return;
    await setAliasesFromApi(dispatch, getUserId(getState));
};

const TERMINAL_ERROR_TYPE_TO_STATUS: Partial<Record<EmailAliasErrorType, EmailAliasConfirmationStatus>> = {
    alreadyConfirmed: 'alreadyConfirmed',
    notFound: 'notFound',
    forbidden: 'forbidden'
};

export const startEmailAliasConfirmation = (aliasId: string) => async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const userId = getUserId(getState);
    dispatch(setConfirmation({...initialConfirmation, status: 'requesting', aliasId}));
    try {
        const response = await userApi.requestEmailAliasConfirmationCode({userId, aliasId});
        const alias = getAliasWithoutToken(getResponseBody<AliasResponse>(response));
        if (getIsCurrentConfirmation(getState, aliasId)) {
            dispatch(setConfirmation({status: 'codeSent', alias}));
        }
    } catch (error) {
        const errorType = getEmailAliasErrorType(error);
        if (errorType === 'cooldown') {
            try {
                const aliases = await getAliasesFromApi(userId);
                dispatch(setAliases(aliases));
                const alias = aliases.find(({id}) => id === aliasId) ?? null;
                if (getIsCurrentConfirmation(getState, aliasId)) {
                    dispatch(setConfirmation({status: 'cooldown', alias}));
                }
            } catch {
                if (getIsCurrentConfirmation(getState, aliasId)) {
                    dispatch(setConfirmation({status: 'error'}));
                }
            }
            return;
        }
        if (getIsCurrentConfirmation(getState, aliasId)) {
            dispatch(setConfirmation({status: TERMINAL_ERROR_TYPE_TO_STATUS[errorType] ?? 'error'}));
        }
    }
};

export const confirmEmailAliasCode = ({aliasId, code}: {aliasId: string; code: string}) => async (dispatch: AppDispatch, getState: () => RootState): Promise<EmailAliasErrorType | null> => {
    const userId = getUserId(getState);
    const formStatus: EmailAliasConfirmationStatus = getState().emailAliases.confirmation.status === 'cooldown' ? 'cooldown' : 'codeSent';
    dispatch(setConfirmation({status: 'confirming', codeErrorType: null}));
    try {
        const response = await userApi.confirmEmailAlias({userId, aliasId, data: {confirmation_token: Number(code)}});
        const alias = getAliasWithoutToken(getResponseBody<AliasResponse>(response));
        if (getIsCurrentConfirmation(getState, aliasId)) {
            dispatch(setConfirmation({status: 'confirmed', alias}));
        }
        return null;
    } catch (error) {
        const errorType = getEmailAliasErrorType(error);
        if (getIsCurrentConfirmation(getState, aliasId)) {
            const terminalStatus = TERMINAL_ERROR_TYPE_TO_STATUS[errorType];
            dispatch(setConfirmation(terminalStatus ? {status: terminalStatus} : {status: formStatus, codeErrorType: errorType}));
        }
        return errorType;
    }
};

export const selectEmailAliases = (state: any): UserEmailAlias[] => state.emailAliases.aliases;
export const selectIsEmailAliasesLoading = (state: any): boolean => state.emailAliases.isLoading;
export const selectEmailAliasesUserId = (state: any): string | null => state.emailAliases.aliasesUserId;
export const selectConfirmedEmailAliasEmails = createSelector(
    [selectEmailAliases],
    (aliases): string[] => aliases
        .filter((alias) => alias.confirmed_at !== null)
        .map((alias) => alias.email)
);
export const selectIsEmailAliasAdding = (state: any): boolean => state.emailAliases.isAdding;
export const selectHasPendingEmailAliases = (state: any): boolean => state.emailAliases.aliases.some((alias: UserEmailAlias) => alias.confirmed_at === null);
export const selectBusyEmailAliasIds = (state: any): string[] => state.emailAliases.busyAliasIds;
export const selectEmailAliasConfirmation = (state: any): EmailAliasConfirmation => state.emailAliases.confirmation;

export default emailAliasesSlice.reducer;
